import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isOnOrder, isSoldOut } from "@/lib/patternAvailability";
import { collectionsOf, firstChoosable, getCategory, getProductPatterns, hasOptionExtras, imageSrc, productImages, textureSrc } from "@/lib/catalog";
import { diversePicks } from "@/lib/patternFamilies";
import { ProductVisual } from "./ProductVisual";
import { ProductDesigner } from "./ProductDesigner";

// Peças vendidas à parte sem fotografia própria: o cartão mostra o desenho da peça.
const DRAWN = ["ursinho", "fita-porta-chupetas"];
import { AvailabilityBadge, Price } from "./ShopBits";

const Swipe = ({ photos, name }) => {
  const [i, setI] = useState(0);
  const ref = useRef(null);
  return (
    <div className="absolute inset-0 hidden [@media(hover:none)]:block">
      <div ref={ref} onScroll={(e) => setI(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))} className="flex h-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {photos.map((p, k) => <img key={p.id} src={imageSrc(p.id, 400)} srcSet={`${imageSrc(p.id, 400)} 400w, ${imageSrc(p.id, 1200)} 1200w`} sizes="50vw" alt={k === 0 ? p.alt || name : ""} loading="lazy" draggable={false} className="h-full w-full shrink-0 snap-center object-cover" />)}
      </div>
      {photos.length > 1 && (
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center gap-1">
          {photos.map((p, k) => <span key={p.id} className={`h-1.5 rounded-full bg-ivory shadow-[0_0_0_1px_rgba(64,55,49,.15)] transition-all ${k === i ? "w-4" : "w-1.5 opacity-70"}`} />)}
        </span>
      )}
    </div>
  );
};

export const ProductCard = ({ product, patternId }) => {
  const pats = getProductPatterns(product);
  const first = pats.find((x) => x.id === patternId) ?? firstChoosable(pats) ?? pats[0];
  const allSold = pats.length > 0 && pats.every(isSoldOut);
  const photos = productImages(product, first?.id);
  return (
    <Link to={`/produto/${product.slug}${first && first !== pats[0] && !isSoldOut(first) ? `?tecido=${first.id}` : ""}`} data-testid={`product-card-${product.slug}`} className="group block">
      <div className="relative aspect-[5/6] overflow-hidden rounded-lg bg-linen sm:aspect-[4/5]">
        {photos.length > 0 ? (
          <>
            {/* rato: a segunda fotografia aparece ao pairar */}
            <img src={imageSrc(photos[0].id, 400)} srcSet={`${imageSrc(photos[0].id, 400)} 400w, ${imageSrc(photos[0].id, 1200)} 1200w`} sizes="(min-width: 1024px) 25vw, 50vw" alt={photos[0].alt || product.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] [@media(hover:none)]:hidden" />
            {photos[1] && <img src={imageSrc(photos[1].id, 400)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100 [@media(hover:none)]:hidden" />}
            {/* toque: as fotografias deslizam com o dedo (um toque abre a peça) */}
            <Swipe photos={photos.slice(0, 4)} name={product.name} />
          </>
        ) : DRAWN.includes(product.slug) ? (
          <div className="absolute inset-[6%] transition-transform duration-700 group-hover:scale-[1.04]"><ProductDesigner product={product} texture={first?.texture} patternName={first?.name} /></div>
        ) : (
          <>
        <img
          src={textureSrc(first?.texture ?? "linho")}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100"
        />
        <ProductVisual
          shape={product.shape}
          texture={first?.texture}
          className="absolute inset-[12%] transition-transform duration-700 ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.04] group-hover:opacity-0"
        />
          </>
        )}
      </div>
      {patternId && first?.id === patternId && (isSoldOut(first)
        ? <p className="mt-2 text-xs font-medium text-taupe-strong" data-testid="card-in-pattern">{first.name}: esgotado</p>
        : <p className="mt-2 text-xs font-medium text-bordeaux" data-testid="card-in-pattern">Disponível em {first.name}{isOnOrder(first) ? " (por encomenda)" : ""}</p>)}
      <div className="mt-3 space-y-1 px-0.5 sm:mt-4 sm:space-y-1.5">
        <p className="hidden flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:flex font-medium uppercase tracking-[0.16em] text-taupe">
          {getCategory(product.category)?.name}
          {collectionsOf(product).map((c) => <span key={c.slug} data-testid={`product-collection-label-${c.slug}`} className="rounded-full bg-rose-soft px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-bordeaux">{c.label}</span>)}
        </p>
        <h3 className="font-display text-[17px] leading-tight text-ink sm:text-[19px]">{product.name}</h3>
        <Price product={product} from={hasOptionExtras(product)} className="block text-sm" />
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1 pt-1" aria-label={`${pats.length} padrões`}>
          {diversePicks(pats, 4).map((p) => (
            <img key={p.id} src={textureSrc(p.texture, 240)} alt="" title={p.name} loading="lazy" className="h-[18px] w-[18px] rounded-[4px] object-cover sm:h-4 sm:w-4 shadow-[inset_0_0_0_1px_rgba(64,55,49,.12)]" />
          ))}
          <span className="ml-1 text-xs leading-tight text-taupe">{pats.length === 1 ? "1 padrão" : `Escolhe entre ${pats.length} padrões`}</span>
        </div>
        <div className={allSold ? "pt-1" : "hidden pt-1 sm:block"}>
          {allSold ? <span className="inline-flex items-center gap-[7px] text-xs font-medium text-taupe-strong" data-testid="card-sold-out"><span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />Padrões esgotados</span> : <AvailabilityBadge product={product} />}
        </div>
        {product.personalization && <p className="hidden text-xs sm:block leading-snug text-taupe-strong">Pode levar nome personalizado</p>}
      </div>
    </Link>
  );
};
